import { MenuItem } from "@mui/material";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import timeIcon from "./assets/time.svg";

import { useState, useEffect } from "react";

import styles from "./FilterV2View.module.scss";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";

import utc from "dayjs/plugin/utc"; 
import { AggregateWindow } from "../../../types/types";
import { converteTitle } from "../../../../../common/utils/graph.utils";
import Button from "../../../../../common/components/atom/ButtonV2/Button";

dayjs.extend(utc);

export type Preset = "today" | "this-month" | "this-year";

export interface FilterV2ViewProps {
  aggregateWindow?: AggregateWindow | null;
  setAggregateWindow: (aggregateWindow: AggregateWindow) => void;
  devEuiValue?: string;
  setDevEuiValue?: (devEuiValue: string) => void;
  onSubmit?: () => void;
  isLoading?: boolean;
  handleNextWindow?: () => void;
  handlePreviousWindow?: () => void;
  setDateStart?: (date: Date | null) => void;
  setDateStop?: (date: Date | null) => void;
  dateStart?: Date;
  dateStop?: Date;
  onClickPreset?: (Presets: Preset) => void;
  disabledSubmit?: boolean;
  disabledAggregation?: AggregateWindow[];
  presetWindowActive?: Preset;
}

export default function FilterV2View({
  aggregateWindow,
  setAggregateWindow,
  setDevEuiValue,
  devEuiValue,
  onSubmit,
  isLoading,
  handleNextWindow,
  handlePreviousWindow,
  setDateStart,
  setDateStop,
  dateStart,
  dateStop,
  onClickPreset,
  disabledSubmit,
  disabledAggregation,
  presetWindowActive

}: FilterV2ViewProps) {
  const handleChange = (event: SelectChangeEvent) => {
    setAggregateWindow(event.target.value as AggregateWindow);
  };

  const [open, setOpen] = useState(false);

  // Fonction pour calculer la date suivante/précédente selon aggregateWindow
  // Retourne la date de début de la période suivante/précédente (pour l'affichage du label)
  const getNextPrevDate = (isNext: boolean): Date | null => {
    if (!dateStart || !dateStop || !aggregateWindow) return null;
    
    switch (aggregateWindow) {
      case "1h": {
        // Pour next: le startDate suivant = stopDate actuel (car stopDate est le début du jour suivant)
        // Pour prev: le startDate précédent = startDate actuel - 1 jour
        if (isNext) {
          // Le startDate suivant est le stopDate actuel (début du jour suivant)
          const newDate = new Date(dateStop);
          newDate.setUTCHours(0, 0, 0, 0);
          return newDate;
        } else {
          const newDate = new Date(dateStart);
          newDate.setUTCDate(newDate.getUTCDate() - 1);
          newDate.setUTCHours(0, 0, 0, 0);
          return newDate;
        }
      }
      case "15m": {
        // Pour next: ajouter 4 heures au stopDate actuel
        // Pour prev: soustraire 4 heures au startDate actuel
        const currentDate = isNext ? new Date(dateStop) : new Date(dateStart);
        const newDate = new Date(currentDate);
        newDate.setUTCHours(newDate.getUTCHours() + (isNext ? 0 : -4));
        newDate.setUTCMinutes(0, 0, 0);
        return newDate;
      }
      case "1d": {
        // Pour next: le startDate suivant = stopDate actuel (début du mois suivant)
        // Pour prev: le startDate précédent = startDate actuel - 1 mois
        if (isNext) {
          // Le startDate suivant est le stopDate actuel (début du mois suivant)
          const newDate = new Date(dateStop);
          newDate.setUTCHours(0, 0, 0, 0);
          return newDate;
        } else {
          const newDate = new Date(dateStart);
          newDate.setUTCMonth(newDate.getUTCMonth() - 1);
          newDate.setUTCDate(1);
          newDate.setUTCHours(0, 0, 0, 0);
          return newDate;
        }
      }
      case "1mo": {
        // Pour next: le startDate suivant = stopDate actuel (début de l'année suivante)
        // Pour prev: le startDate précédent = startDate actuel - 1 année
        if (isNext) {
          // Le startDate suivant est le stopDate actuel (début de l'année suivante)
          // S'assurer que c'est bien le 1er janvier à 00:00:00
          const newDate = new Date(dateStop);
          // Extraire l'année du stopDate et créer une nouvelle date au 1er janvier de cette année
          const year = newDate.getUTCFullYear();
          const normalizedDate = new Date(Date.UTC(year, 0, 1, 0, 0, 0, 0));
          return normalizedDate;
        } else {
          // Pour prev, utiliser startDate - 1 année
          const newDate = new Date(dateStart);
          const year = newDate.getUTCFullYear() - 1;
          const normalizedDate = new Date(Date.UTC(year, 0, 1, 0, 0, 0, 0));
          return normalizedDate;
        }
      }
      default:
        return null;
    }
  };

  // Fonction pour formater la date selon le type de période
  const formatPeriodLabel = (date: Date | null, isNext: boolean): string => {
    if (!date) return isNext ? "Suivant" : "Précédent";
    
    switch (aggregateWindow) {
      case "1h": {
        // Format: "Lundi 2 Décembre"
        const dayName = date.toLocaleDateString('fr-FR', { weekday: 'long', timeZone: 'UTC' });
        const dayNumber = date.getUTCDate();
        const monthName = date.toLocaleDateString('fr-FR', { month: 'long', timeZone: 'UTC' });
        return `${dayName.charAt(0).toUpperCase() + dayName.slice(1)} ${dayNumber} ${monthName.charAt(0).toUpperCase() + monthName.slice(1)}`;
      }
      case "1d": {
        // Format: "Mars 2025"
        const monthName = date.toLocaleDateString('fr-FR', { month: 'long', timeZone: 'UTC' });
        const year = date.getUTCFullYear();
        return `${monthName.charAt(0).toUpperCase() + monthName.slice(1)} ${year}`;
      }
      case "1mo": {
        // Format: "2025"
        // S'assurer que la date est bien normalisée au 1er janvier
        const normalizedDate = new Date(Date.UTC(date.getUTCFullYear(), 0, 1, 0, 0, 0, 0));
        return normalizedDate.getUTCFullYear().toString();
      }
      case "15m": {
        // Format: "Lundi 2 Décembre 14h"
        const dayName = date.toLocaleDateString('fr-FR', { weekday: 'long', timeZone: 'UTC' });
        const dayNumber = date.getUTCDate();
        const monthName = date.toLocaleDateString('fr-FR', { month: 'long', timeZone: 'UTC' });
        const hour = date.getUTCHours();
        return `${dayName.charAt(0).toUpperCase() + dayName.slice(1)} ${dayNumber} ${monthName.charAt(0).toUpperCase() + monthName.slice(1)} ${hour}h`;
      }
      default:
        return isNext ? "Suivant" : "Précédent";
    }
  };

  const getTooltipText = (isNext: boolean) => {
    const nextPrevDate = getNextPrevDate(isNext);
    return formatPeriodLabel(nextPrevDate, isNext);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  // Gestion de l'escape key et du clic extérieur
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [open]);

  const handleDateStartChange = (date: Dayjs | null) => {
    const dateValue = date?.utc().toDate() || null;
    setDateStart && setDateStart(dateValue);
  };

  const handleDateStopChange = (date: Dayjs | null) => {
    const dateValue = date?.utc().toDate() || null;
    setDateStop && setDateStop(dateValue);
  };

  const handleOnClickPreset = (preset: Preset) => {
      onClickPreset && onClickPreset(preset);
      handleClose();
  }

  const checkDisabledAggregation = (value: AggregateWindow) => {
    return disabledAggregation?.includes(value);
  }

  const getAggregationLabel = (value: AggregateWindow) => {
    switch (value) {
      case "15m":
        return "Par 15 minutes";
      case "1h":
        return "Par heure";
      case "1d":
        return "Par jour";
      case "1mo":
        return "Par mois";
      default:
        return value;
    }
  };

  const getPresetLabel = (preset: Preset) => {
    switch (preset) {
      case "today":
        return "Aujourd'hui";
      case "this-month":
        return "Ce mois-ci";
      case "this-year":
        return "Cette année";
      default:
        return preset;
    }
  };

  const getAggregationDisplay = (agg: AggregateWindow | null) => {
    switch (agg) {
      case "15m":
        return "Par 15 minutes";
      case "1h":
        return "Par heure";
      case "1d":
        return "Par jour";
      case "1mo":
        return "Par mois";
      default:
        return "Non défini";
    }
  };

  // Fonction pour vérifier si la période est un preset
  const isPresetPeriod = (): boolean => {
    return !!presetWindowActive;
  };

  // Fonction pour vérifier si c'est une période standard (un jour, un mois, une année, 4 heures)
  const isStandardPeriod = (): boolean => {
    if (!dateStart || !dateStop || !aggregateWindow) return false;
    
    const start = new Date(dateStart);
    const stop = new Date(dateStop);
    
    switch (aggregateWindow) {
      case "15m": {
        // Vérifier si c'est exactement 4 heures
        const diff = stop.getTime() - start.getTime();
        const fourHours = 4 * 60 * 60 * 1000;
        return Math.abs(diff - fourHours) < 1000; // Tolérance de 1 seconde
      }
      case "1h": {
        // Vérifier si c'est exactement un jour
        const diff = stop.getTime() - start.getTime();
        const oneDay = 24 * 60 * 60 * 1000;
        return Math.abs(diff - oneDay) < 1000; // Tolérance de 1 seconde
      }
      case "1d": {
        // Vérifier si c'est exactement un mois (du 1er au 1er du mois suivant)
        const startMonth = start.getUTCMonth();
        const startYear = start.getUTCFullYear();
        const stopMonth = stop.getUTCMonth();
        const stopYear = stop.getUTCFullYear();
        const isStartOfMonth = start.getUTCDate() === 1 && start.getUTCHours() === 0;
        const isStartOfNextMonth = stop.getUTCDate() === 1 && stop.getUTCHours() === 0;
        const isOneMonth = (stopYear === startYear && stopMonth === startMonth + 1) || 
                          (stopYear === startYear + 1 && stopMonth === 0 && startMonth === 11);
        return isStartOfMonth && isStartOfNextMonth && isOneMonth;
      }
      case "1mo": {
        // Vérifier si c'est exactement une année (du 1er janvier au 1er janvier suivant)
        // Normaliser les dates pour la comparaison
        const startNormalized = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate(), start.getUTCHours(), 0, 0, 0));
        const stopNormalized = new Date(Date.UTC(stop.getUTCFullYear(), stop.getUTCMonth(), stop.getUTCDate(), stop.getUTCHours(), 0, 0, 0));
        
        const isStartOfYear = startNormalized.getUTCMonth() === 0 && startNormalized.getUTCDate() === 1 && startNormalized.getUTCHours() === 0;
        const isStartOfNextYear = stopNormalized.getUTCMonth() === 0 && stopNormalized.getUTCDate() === 1 && stopNormalized.getUTCHours() === 0;
        const isOneYear = stopNormalized.getUTCFullYear() === startNormalized.getUTCFullYear() + 1;
        return isStartOfYear && isStartOfNextYear && isOneYear;
      }
      default:
        return false;
    }
  };

  // Fonction pour formater la période du bouton
  const formatPeriodButtonText = (): string => {
    if (!dateStart || !dateStop || !aggregateWindow) {
      return converteTitle(dateStart?.toISOString() || "", dateStop?.toISOString() || "");
    }
    
    // Pour le mode 1mo, toujours afficher juste l'année
    if (aggregateWindow === "1mo") {
      const normalizedDate = new Date(Date.UTC(dateStart.getUTCFullYear(), 0, 1, 0, 0, 0, 0));
      return normalizedDate.getUTCFullYear().toString();
    }
    
    const isPreset = isPresetPeriod();
    const isStandard = isStandardPeriod();
    
    // Si c'est un preset ou une période standard, afficher le format simplifié
    if (isPreset || isStandard) {
      switch (aggregateWindow) {
        case "15m": {
          // Format: "Lundi 2 Décembre 14h"
          const dayName = dateStart.toLocaleDateString('fr-FR', { weekday: 'long', timeZone: 'UTC' });
          const dayNumber = dateStart.getUTCDate();
          const monthName = dateStart.toLocaleDateString('fr-FR', { month: 'long', timeZone: 'UTC' });
          const hour = dateStart.getUTCHours();
          return `${dayName.charAt(0).toUpperCase() + dayName.slice(1)} ${dayNumber} ${monthName.charAt(0).toUpperCase() + monthName.slice(1)} ${hour}h`;
        }
        case "1h": {
          // Format: "Lundi 2 Décembre"
          const dayName = dateStart.toLocaleDateString('fr-FR', { weekday: 'long', timeZone: 'UTC' });
          const dayNumber = dateStart.getUTCDate();
          const monthName = dateStart.toLocaleDateString('fr-FR', { month: 'long', timeZone: 'UTC' });
          return `${dayName.charAt(0).toUpperCase() + dayName.slice(1)} ${dayNumber} ${monthName.charAt(0).toUpperCase() + monthName.slice(1)}`;
        }
        case "1d": {
          // Format: "Janvier 2025"
          const monthName = dateStart.toLocaleDateString('fr-FR', { month: 'long', timeZone: 'UTC' });
          const year = dateStart.getUTCFullYear();
          return `${monthName.charAt(0).toUpperCase() + monthName.slice(1)} ${year}`;
        }
        default:
          return converteTitle(dateStart.toISOString(), dateStop.toISOString());
      }
    }
    
    // Si c'est personnalisé, utiliser le format par défaut
    return converteTitle(dateStart.toISOString(), dateStop.toISOString());
  };

  const getButtonDisplayText = () => {
    const periodText = formatPeriodButtonText();
    const isPreset = isPresetPeriod();
    
    // Si c'est un preset, afficher seulement la période sans l'agrégation
    if (isPreset) {
      return periodText;
    }
    
    // Si c'est personnalisé, afficher la période avec l'agrégation
    const aggregationText = getAggregationDisplay(aggregateWindow || null);
    return `${periodText} • ${aggregationText}`;
  };

  // Fonction pour vérifier si le bouton next doit être désactivé
  const isNextButtonDisabled = (): boolean => {
    if (!dateStart || !dateStop || !aggregateWindow) return false;
    
    // Calculer la date de début de la période suivante
    const nextPeriodStartDate = getNextPrevDate(true);
    if (!nextPeriodStartDate) return false;
    
    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);
    today.setUTCMinutes(0, 0, 0);
    today.setUTCSeconds(0, 0);
    today.setUTCMilliseconds(0);
    
    const nextStart = new Date(nextPeriodStartDate);
    nextStart.setUTCHours(0, 0, 0, 0);
    nextStart.setUTCMinutes(0, 0, 0);
    nextStart.setUTCSeconds(0, 0);
    nextStart.setUTCMilliseconds(0);
    
    // Désactiver si la date de début de la période suivante est strictement supérieure à aujourd'hui
    // (on permet d'aller jusqu'à aujourd'hui inclus, mais pas au-delà)
    return nextStart.getTime() > today.getTime();
  };

  return (
    <>
      <div className={styles["filter-v2"]}>
        {handlePreviousWindow && (
          <button
            onClick={handlePreviousWindow}
            className={styles["nav-button"]}
            aria-label="Fenêtre précédente"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className={styles["nav-button-label"]}>{getTooltipText(false)}</span>
          </button>
        )}
        <button
          onClick={handleOpen}
          className={styles["filter-button"]}
        >
          <div className={styles["button-content"]}>
            <img className={styles['icon-time']} src={timeIcon} alt="time" />
            <span className={styles["button-text"]}>
              {getButtonDisplayText()}
            </span>
            <svg className={styles["arrow-icon"]} width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </button>
        {handleNextWindow && (
          <button
            onClick={handleNextWindow}
            className={styles["nav-button"]}
            aria-label="Fenêtre suivante"
            disabled={isNextButtonDisabled()}
            style={isNextButtonDisabled() ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
          >
            <span className={styles["nav-button-label"]}>{getTooltipText(true)}</span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
      </div>

      {/* Modal native sans composant Modal stylisé */}
      {open && (
        <div className={styles["modal-overlay"]} onClick={handleClose}>
          <div className={styles["modal-container"]} onClick={(e) => e.stopPropagation()}>
            <div className={styles["modal-header"]}>
              <h2 className={styles["modal-title"]}>Filtres de période</h2>
              <p className={styles["modal-subtitle"]}>Configurez la période et l'agrégation des données</p>
            </div>

            <div className={styles["modal-content"]}>
              <div className={styles["section"]}>
                <div className={styles["section-header"]}>
                  <h3 className={styles["section-title"]}>Agrégation</h3>
                  <p className={styles["section-description"]}>Choisissez la granularité des données</p>
                </div>
                
                <div className={styles["select-container"]}>
                  <Select
                    labelId="aggregation-select-label"
                    id="aggregation-select"
                    value={aggregateWindow || ""}
                    onChange={handleChange}
                    className={styles["select"]}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      <em>Sélectionner une agrégation</em>
                    </MenuItem>
                    <MenuItem value={"15m"} disabled={checkDisabledAggregation('15m')}>
                      {getAggregationLabel('15m')}
                    </MenuItem>
                    <MenuItem value={"1h"} disabled={checkDisabledAggregation('1h')}>
                      {getAggregationLabel('1h')}
                    </MenuItem>
                    <MenuItem value={"1d"} disabled={checkDisabledAggregation('1d')}>
                      {getAggregationLabel('1d')}
                    </MenuItem>
                    <MenuItem value={"1mo"} disabled={checkDisabledAggregation('1mo')}>
                      {getAggregationLabel('1mo')}
                    </MenuItem>
                  </Select>
                </div>
              </div>

              <div className={styles["section"]}>
                <div className={styles["section-header"]}>
                  <h3 className={styles["section-title"]}>Période</h3>
                  <p className={styles["section-description"]}>Définissez l'intervalle de temps</p>
                </div>

                {onClickPreset && (
                  <div className={styles["preset-section"]}>
                    <h4 className={styles["preset-title"]}>Périodes prédéfinies</h4>
                    <div className={styles["preset-grid"]}>
                      {(["today", "this-month", "this-year"] as Preset[]).map((preset) => (
                        <Button
                          key={preset}
                          onClick={() => handleOnClickPreset(preset)}
                          variant={presetWindowActive === preset ? "contained" : "bordered"}
                          color="primary"
                          style={{ height: 44, minWidth: 120 }}
                          size="small"
                        >
                          {getPresetLabel(preset)}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                <div className={styles["custom-section"]}>
                  <h4 className={styles["custom-title"]}>Période personnalisée</h4>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <div className={styles["date-pickers"]}>
                      <div className={styles["date-picker-container"]}>
                        <DatePicker
                          label="Date de début"
                          onChange={handleDateStartChange}
                          value={dateStart && dayjs(dateStart)}
                          format="DD/MM/YYYY"
                          timezone="UTC"
                          className={styles["date-picker"]}
                        />
                      </div>
                      <div className={styles["date-picker-container"]}>
                        <DatePicker
                          label="Date de fin"
                          onChange={handleDateStopChange}
                          value={dateStop && dayjs(dateStop)}
                          minDate={dateStart && dayjs(dateStart)}
                          format="DD/MM/YYYY"
                          timezone="UTC"
                          className={styles["date-picker"]}
                        />
                      </div>
                    </div>
                  </LocalizationProvider>
                </div>
              </div>
            </div>

            <div className={styles["modal-footer"]}>
              <div className={styles["button-group"]}>
                <Button
                  onClick={handleClose}
                  variant="bordered"
                  color="primary"
                  style={{ height: 44, minWidth: 100 }}
                >
                  Annuler
                </Button>
                <Button
                  onClick={() => {
                    onSubmit && onSubmit();
                    handleClose();
                  }}
                  color="primary"
                  variant="contained"
                  style={{ height: 44, minWidth: 120 }}
                  disabled={disabledSubmit}
                >
                  Appliquer
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
